import type { FC, PropsWithChildren } from "react"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useMachine } from "@xstate/react"
import { useDisclosure } from "@chakra-ui/react"

import { useServices } from "@/identity/useServices"
import SmartContractWalletAuthenticationWarning from "@/identity/SmartContractWalletAuthenticationWarningModal"
import { useMe } from "@/user/providers/MeProvider"
import identityMachine, { IdentityMachineEvents } from "@/identity/machine"
import WalletSelectionModal from "@/web3/components/WalletSelectionModal"
import { useSignerStore } from "@/web3/providers/SignerProvider"
import { Chain, useMultichainAccount } from "@/web3/hooks/useMultichainAccount"
import { useToast } from "@/common/hooks/useToast"
import { useLogout } from "@/session/hooks/useLogout"

type ConnectWalletProps = {
  shouldPromptAuthenticationAfterWalletConnection?: boolean
  redirectTo?: string
  walletType?: Chain
}

type OnAuthenticateProps = {
  redirectTo?: string
}

type IdentityContextType = {
  isAuthenticated: boolean
  isWalletConnected: boolean
  isCompletedAndFirstVisit: boolean
  isSmartContractWallet: boolean
  connectWallet: (props?: ConnectWalletProps) => void
  authenticate: (props?: OnAuthenticateProps) => void
  logout: (props?: OnAuthenticateProps) => void
}

const IdentityContext = createContext<IdentityContextType | null>(null)

const IdentityProvider: FC<PropsWithChildren> = ({ children }) => {
  const { multichainAccount } = useMultichainAccount()
  const { signer } = useSignerStore()
  const me = useMe()
  const { logout: _logout } = useLogout()
  const { toast } = useToast()
  const hasPromptedAuthentication = useRef(false)
  const walletSelectionDisclosure = useDisclosure()
  const [walletType, setWalletType] = useState<Chain | undefined>()

  const {
    checkIsSmartContractWallet,
    fetchNonce,
    generateMessage,
    generateSafeTransactionUrl,
    signMessage,
    login,
  } = useServices()
  const [state, send] = useMachine(identityMachine, {
    services: {
      checkIsSmartContractWallet,
      fetchNonce,
      generateMessage,
      generateSafeTransactionUrl,
      signMessage,
      login,
    },
  })

  const { address, chain } = multichainAccount
  const isConnected = Boolean(address)

  useEffect(() => {
    if (state.hasTag("renderErrorToast")) {
      toast({
        title: "Error",
        description: "An error occurred",
        status: "error",
      })
    }
  }, [state, toast])

  // Core functions
  const connectWallet = useCallback(
    (props?: ConnectWalletProps) => {
      setWalletType(props?.walletType)

      walletSelectionDisclosure.onOpen()

      send(IdentityMachineEvents.INITIALIZE_CONTEXT, {
        shouldPromptAuthenticationAfterWalletConnection:
          props?.shouldPromptAuthenticationAfterWalletConnection ?? false,
        redirectTo: props?.redirectTo,
      })
    },
    [walletSelectionDisclosure, send],
  )
  // Handle wallet connection state changes
  useEffect(() => {
    if (isConnected) {
      if (chain === Chain.EVM && signer?.provider) {
        send(IdentityMachineEvents.WALLET_CONNECTED, {
          chain,
        })
      }

      if (chain === Chain.SOL) {
        send(IdentityMachineEvents.WALLET_CONNECTED, {
          chain,
        })
      }
    }

    if (!isConnected) {
      send(IdentityMachineEvents.WALLET_DISCONNECTED)
      hasPromptedAuthentication.current = false
    }
  }, [isConnected, send, signer?.provider, chain])

  // Handle authentication state change
  useEffect(() => {
    if (Boolean(me) && state.can(IdentityMachineEvents.AUTHENTICATED)) {
      send(IdentityMachineEvents.AUTHENTICATED)
    }

    if (!Boolean(me) && state.can(IdentityMachineEvents.LOGGED_OUT)) {
      send(IdentityMachineEvents.LOGGED_OUT)
    }
  }, [me, send, state])

  const authenticate = useCallback(
    (props?: OnAuthenticateProps) => {
      send(IdentityMachineEvents.AUTHENTICATE, {
        redirectTo: props?.redirectTo,
      })
    },
    [send],
  )

  const logout = useCallback(
    (props?: OnAuthenticateProps) => {
      _logout(props?.redirectTo)
    },
    [_logout],
  )

  const handleContinue = useCallback(() => {
    send(
      IdentityMachineEvents.ACKNOWLEDGE_INITIAL_SMART_CONTRACT_WALLET_WARNING,
    )
  }, [send])

  const handleCancel = useCallback(() => {
    send(IdentityMachineEvents.CANCEL_SMART_CONTRACT_WALLET_AUTHENTICATION)
  }, [send])

  const isWarningStepLoading = useMemo(() => {
    return (
      state.matches(
        "walletConnected.smartContractWallet.authenticate.fetchNonce",
      ) ||
      state.matches(
        "walletConnected.smartContractWallet.authenticate.generateMessage",
      ) ||
      state.matches(
        "walletConnected.smartContractWallet.authenticate.generateSafeTransactionUrl",
      )
    )
  }, [state])

  const step = useMemo(() => {
    if (
      state.matches(
        "walletConnected.smartContractWallet.authenticate.displaySmartContractWalletWarning",
      ) ||
      state.matches(
        "walletConnected.smartContractWallet.authenticate.fetchNonce",
      ) ||
      state.matches(
        "walletConnected.smartContractWallet.authenticate.generateMessage",
      ) ||
      state.matches(
        "walletConnected.smartContractWallet.authenticate.generateSafeTransactionUrl",
      )
    ) {
      return "warning"
    }

    return "signatureCollection"
  }, [state])

  const isSmartContractWalletAuthenticationWarningOpen = useMemo(() => {
    return (
      state.matches("walletConnected.smartContractWallet.authenticate") &&
      !state.matches(
        "walletConnected.smartContractWallet.authenticate.authenticated.complete",
      )
    )
  }, [state])

  const value = useMemo(
    () => ({
      isAuthenticated: state.hasTag("authenticated"),
      isWalletConnected: state.matches("walletConnected"),
      isCompletedAndFirstVisit:
        (state.hasTag("idleWallet") || state.hasTag("authenticated")) &&
        state.context.isUserFirstVisit,
      isSmartContractWallet: state.matches(
        "walletConnected.smartContractWallet",
      ),
      connectWallet,
      authenticate,
      logout,
    }),
    [state, connectWallet, authenticate, logout],
  )

  return (
    <IdentityContext.Provider value={value}>
      {children}
      <WalletSelectionModal
        isOpen={walletSelectionDisclosure.isOpen}
        walletType={walletType}
        onClose={() => walletSelectionDisclosure.onClose()}
      />
      <SmartContractWalletAuthenticationWarning
        isLoading={isWarningStepLoading}
        isOpen={isSmartContractWalletAuthenticationWarningOpen}
        safeTransactionUrl={state.context.safeTransactionUrl}
        signatureError={state.context.signatureError}
        step={step}
        onCancel={handleCancel}
        onClose={() => console.log("CLOSE")}
        onContinue={handleContinue}
      />
    </IdentityContext.Provider>
  )
}

export default IdentityProvider

export const useIdentity = () => {
  const context = useContext(IdentityContext)

  if (!context) {
    throw new Error("useIdentity must be used within an IdentityProvider")
  }

  return context
}
